/**
 * Curated list of Israeli municipalities for the pilot screening UI, with rough
 * city-centre coordinates so the panel can pre-select the right one from the
 * Forma project's geolocation.
 *
 * Screening-only: only Tel Aviv is the validated pilot (`pilot: true`). The other
 * entries let the user run the screening pipeline against their location, but
 * carry no municipal-compliance guarantee — the UI labels them accordingly.
 */
export interface Municipality {
  id: string;
  label: string;
  /** Approximate city-centre latitude (WGS84). */
  lat: number;
  /** Approximate city-centre longitude (WGS84). */
  lon: number;
  /** True for the validated pilot municipality (Tel Aviv). */
  pilot?: boolean;
}

export const MUNICIPALITIES: readonly Municipality[] = [
  { id: 'tel-aviv', label: 'Tel Aviv-Yafo', lat: 32.0853, lon: 34.7818, pilot: true },
  { id: 'jerusalem', label: 'Jerusalem', lat: 31.7683, lon: 35.2137 },
  { id: 'haifa', label: 'Haifa', lat: 32.794, lon: 34.9896 },
  { id: 'rishon-lezion', label: 'Rishon LeZion', lat: 31.973, lon: 34.7925 },
  { id: 'petah-tikva', label: 'Petah Tikva', lat: 32.084, lon: 34.8878 },
  { id: 'ashdod', label: 'Ashdod', lat: 31.8044, lon: 34.6553 },
  { id: 'netanya', label: 'Netanya', lat: 32.3215, lon: 34.8532 },
  { id: 'beer-sheva', label: "Be'er Sheva", lat: 31.2518, lon: 34.7913 },
  { id: 'holon', label: 'Holon', lat: 32.0167, lon: 34.7792 },
  { id: 'ramat-gan', label: 'Ramat Gan', lat: 32.0684, lon: 34.8248 },
  { id: 'bat-yam', label: 'Bat Yam', lat: 32.0171, lon: 34.7457 },
  { id: 'herzliya', label: 'Herzliya', lat: 32.1624, lon: 34.8447 },
  { id: 'kfar-saba', label: 'Kfar Saba', lat: 32.175, lon: 34.907 },
  { id: 'raanana', label: "Ra'anana", lat: 32.1848, lon: 34.8713 },
];

export function findMunicipality(id: string): Municipality | undefined {
  return MUNICIPALITIES.find((municipality) => municipality.id === id);
}

const EARTH_RADIUS_KM = 6371;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Great-circle distance between two WGS84 points, in kilometres (haversine). */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(a));
}

/**
 * Returns the closest known municipality to a coordinate, or undefined when the
 * nearest one is farther than `maxKm` (e.g. a project outside Israel). The
 * default radius keeps a match plausibly "in" the city's metro area.
 */
export function nearestMunicipality(
  lat: number,
  lon: number,
  maxKm = 30,
): Municipality | undefined {
  let best: Municipality | undefined;
  let bestDistance = Infinity;

  for (const municipality of MUNICIPALITIES) {
    const d = distanceKm(lat, lon, municipality.lat, municipality.lon);
    if (d < bestDistance) {
      bestDistance = d;
      best = municipality;
    }
  }

  return bestDistance <= maxKm ? best : undefined;
}
